import { db } from "@/main";
import { firestore } from "firebase";
import {
  isValidRenoRating,
  RENO_COMMENT_MAX_LENGTH,
  RENO_NOTES_SUBCOLLECTION,
  RENO_REVIEW_DEVICES_COLLECTION,
  RENO_REVIEW_QUEUE_COLLECTION,
  RENO_REVIEWERS_COLLECTION,
  RENO_REVIEWS_SUBCOLLECTION,
  RENO_SEEN_SUBCOLLECTION,
  RENO_SUPPRESSIONS_SUBCOLLECTION,
  emptyReviewFilters,
  renoNoteDocId,
  renoReviewDocId,
  renoReviewQueueDocId,
  renoSeenDocId,
  renoSuppressionDocId,
  RenoReview,
  RenoReviewDevice,
  RenoReviewNote,
  RenoReviewDeviceType,
  RenoReviewFilters,
  RenoReviewQueueCount,
  RenoReviewQueueEntry,
  RenoReviewSeen,
  RenoReviewSuppression,
} from "@/data/models/RenoReviewEntry";

/**
 * Reads the RENO review queue index that IRES Assist maintains.
 *
 * Read-only by design: these collections are written exclusively by Assist through the Admin SDK,
 * and the security rules deny client writes outright.
 *
 * **Every query here is shaped to a deployed composite index.** Changing a filter or an `orderBy`
 * without adding the matching index turns the screen into a runtime `FAILED_PRECONDITION`, which no
 * typecheck or unit test catches. The four deployed indexes are:
 *
 * ```
 * renoReviewDevices (modelVersion, serialNo)
 * renoReviewDevices (modelVersion, deviceType, serialNo)
 * renoReviewQueue   (serialNo, modelVersion, chainStart DESC)
 * renoReviewQueue   (serialNo, modelVersion, isActive, chainStart DESC)
 * ```
 *
 * The queue count ({@link countReviewQueue}) deliberately needs **none** of them: it filters on one
 * field at a time and orders by that field or by `__name__`, which is precisely the shape of the
 * automatic single-field index Firestore maintains for every field. Everything else it evaluates in
 * memory. That keeps a screen whose filter combinations are numerous from needing an index per
 * combination.
 */

/**
 * Cap on documents read per queue count. See {@link countReviewQueue}.
 *
 * The queue holds tens of thousands of dryings and `firebase@7.24.0` has no `count()` aggregation, so
 * an unfiltered total would mean reading all of them on every filter change. Beyond this the figure
 * is reported as a lower bound instead.
 */
export const REVIEW_COUNT_CAP = 1000;

/**
 * Cap on documents read from one reviewer's own stores per count.
 *
 * Higher than {@link REVIEW_COUNT_CAP} because these sets are *subtracted*: truncating them silently
 * inflates the figure, whereas truncating the scan merely bounds it. One document per drying the
 * reviewer has touched, so reaching this means a pass of 5000 dryings.
 */
export const REVIEW_HISTORY_CAP = 5000;

/** Firestore rejects `in` filters with more than 10 values. */
const MAX_IN_VALUES = 10;

function devicesCollection() {
  return db.collection(RENO_REVIEW_DEVICES_COLLECTION);
}

function queueCollection() {
  return db.collection(RENO_REVIEW_QUEUE_COLLECTION);
}

/**
 * Applies the device-track filters.
 *
 * The device track carries **equality filters only**, which is precisely why it can be ordered by
 * `serialNo` — "start at device xxx" is just a cursor on this query. Adding a range filter here
 * would force `orderBy` onto that field and break the traversal.
 *
 * `deviceTypes` is truncated to Firestore's 10-value `in` limit rather than throwing: the enum has
 * six members, so the limit is unreachable through the UI, and silently degrading beats a crash if
 * that ever changes.
 */
function applyDeviceFilters(
  ref: firestore.Query,
  filters: RenoReviewFilters
): firestore.Query {
  let query = ref.where("modelVersion", "==", filters.modelVersion);

  if (filters.deviceTypes.length > 0) {
    query = query.where(
      "deviceType",
      "in",
      filters.deviceTypes.slice(0, MAX_IN_VALUES)
    );
  }

  return query.orderBy("serialNo");
}

/**
 * Loads a page of the device track, ascending by serial number.
 *
 * @param filters Reviewer's filter selection.
 * @param pageSize How many devices to fetch.
 * @param startAtSerial Serial to resume from, inclusive. This is the "starting with device xxx"
 *   requirement, expressed as a cursor rather than a filter.
 * @returns The devices in serial order.
 */
export async function loadReviewDevices(
  filters: RenoReviewFilters,
  pageSize: number,
  startAtSerial?: string | null
): Promise<RenoReviewDevice[]> {
  let query = applyDeviceFilters(devicesCollection(), filters);

  if (startAtSerial) {
    query = query.startAt(startAtSerial);
  }

  const snapshot = await query.limit(pageSize).get();

  return snapshot.docs.map((doc) => doc.data() as RenoReviewDevice);
}

/**
 * Which device types actually appear in the index for one model version.
 *
 * Firestore has no `DISTINCT`, and reading the whole device track to find out would be thousands of
 * documents for a dropdown. Instead this asks each candidate type whether **one** device exists —
 * `limit(1)` per type, so six reads total, and each probe is the deployed
 * `(modelVersion, deviceType, serialNo)` index.
 *
 * Worth doing because the enum lies about production: every indexed device is `BOX`, so a dropdown
 * built from the enum offered five options that guaranteed an empty queue.
 *
 * @returns The types present, in enum order. Empty only if the index holds nothing at all.
 */
export async function loadAvailableDeviceTypes(
  modelVersion: string
): Promise<RenoReviewDeviceType[]> {
  const candidates = Object.values(RenoReviewDeviceType);

  const present = await Promise.all(
    candidates.map(async (deviceType) => {
      const snapshot = await applyDeviceFilters(devicesCollection(), {
        ...emptyReviewFilters(modelVersion),
        deviceTypes: [deviceType],
      })
        .limit(1)
        .get();

      return snapshot.size > 0 ? deviceType : null;
    })
  );

  return present.filter(
    (deviceType): deviceType is RenoReviewDeviceType => deviceType !== null
  );
}

/**
 * Cutoff date for the "not older than xx months" filter, measured from chain start (D13).
 *
 * Exported for testing: month arithmetic is easy to get subtly wrong, and `setMonth` handling of
 * month-end dates is worth pinning.
 *
 * @param months How many months back to look.
 * @returns The earliest `chainStart` that still qualifies.
 */
export function cutoffForMonths(months: number): Date {
  const cutoff = new Date();

  cutoff.setMonth(cutoff.getMonth() - months);

  return cutoff;
}

/**
 * The single `chainStart >=` bound these filters imply, whichever way the reviewer expressed it.
 *
 * "Not older than N months" and "from this date" are the same constraint, and there is only one range
 * slot: a query may have exactly one range-filtered field, and it must be the first `orderBy`. Keeping
 * the resolution here means every query, both count strategies and the traversal agree on it.
 *
 * An absolute date wins over a month count, being the more specific request, though the UI offers them
 * as alternatives so both are rarely set.
 *
 * @returns The earliest qualifying `chainStart`, or null when the pass is not time-bounded.
 */
export function chainStartCutoff(filters: RenoReviewFilters): Date | null {
  if (filters.fromDate) {
    return filters.fromDate;
  }

  return filters.maxAgeMonths != null
    ? cutoffForMonths(filters.maxAgeMonths)
    : null;
}

/**
 * Loads one device's reviewable chains, newest first.
 *
 * `chainStart` is both the range-filtered field and the first `orderBy`, which is what satisfies
 * Firestore's ordering constraint.
 *
 * The age filter and the active-only filter are **mutually exclusive**: each maps to a different
 * deployed index, and combining them would need a fifth. When both are somehow set, the age filter
 * wins, because "only dryings not older than xx months" is the more specific request.
 *
 * @param serialNo Device to load chains for.
 * @param filters Reviewer's filter selection.
 * @returns The device's qualifying chains, `chainStart` descending.
 */
export async function loadChainsForDevice(
  serialNo: string,
  filters: RenoReviewFilters
): Promise<RenoReviewQueueEntry[]> {
  let query: firestore.Query = queueCollection()
    .where("serialNo", "==", serialNo)
    .where("modelVersion", "==", filters.modelVersion);

  const cutoff = chainStartCutoff(filters);

  if (cutoff) {
    query = query.where("chainStart", ">=", cutoff);
  } else if (filters.activeOnly) {
    query = query.where("isActive", "==", true);
  }

  const snapshot = await query.orderBy("chainStart", "desc").get();

  return snapshot.docs.map((doc) => doc.data() as RenoReviewQueueEntry);
}

/**
 * A reviewer's verdicts, one per drying **per model version**.
 *
 * Under the reviewer rather than under the drying because the ticket asks for independent per-person
 * ratings, and because it makes the security rule a path comparison: a reviewer can only ever touch
 * documents beneath their own key.
 */
function reviewsCollection(userKey: string) {
  return db
    .collection(RENO_REVIEWERS_COLLECTION)
    .doc(userKey)
    .collection(RENO_REVIEWS_SUBCOLLECTION);
}

/** A reviewer's permanent, version-independent hide list. */
function suppressionsCollection(userKey: string) {
  return db
    .collection(RENO_REVIEWERS_COLLECTION)
    .doc(userKey)
    .collection(RENO_SUPPRESSIONS_SUBCOLLECTION);
}

/**
 * Splits a reviewer key back into its client and user ids.
 *
 * Split on the **first** separator only: client ids are opaque and a user id could in principle
 * contain the separator, so anything after the first one belongs to the user.
 *
 * Returns an object rather than a tuple deliberately — this repo's ESLint cannot parse a tuple
 * return type and fails the whole file with "Cannot read properties of undefined".
 */
function splitReviewerKey(userKey: string): { clientId: string; userId: string } {
  const separator = userKey.indexOf("__");

  return separator < 0
    ? { clientId: userKey, userId: "" }
    : {
        clientId: userKey.slice(0, separator),
        userId: userKey.slice(separator + 2),
      };
}

/**
 * Saves one reviewer's verdict on one drying, overwriting any earlier verdict **of the same model
 * version**.
 *
 * A verdict on a different model version is a different document, so a new RENO release re-opens the
 * drying instead of erasing what the previous model was judged on (D2).
 *
 * @param userKey From {@link renoReviewerKey}. Must identify the signed-in user; the rules enforce it.
 * @param entry The drying being rated, which also supplies the context snapshot.
 * @param rating 1-4, bad to good. Suppression is {@link saveSuppression}, not a rating of 0.
 * @param nickname Display name captured at rating time, so exports stay readable after a rename.
 * @returns The document that was written, so the caller can treat it as "what is stored" without a
 *   second read. A rating on the last drying of a pass moves nowhere, so nothing else would refresh it.
 */
export async function saveReview(
  userKey: string,
  entry: RenoReviewQueueEntry,
  rating: number,
  nickname: string | null
): Promise<RenoReview> {
  if (!isValidRenoRating(rating)) {
    throw new Error(`Refusing to store an out-of-range RENO rating: ${rating}`);
  }

  const { clientId, userId } = splitReviewerKey(userKey);
  const now = firestore.Timestamp.now();

  const payload: RenoReview = {
    userKey,
    clientId,
    userId,
    ratedByNickname: nickname,

    serialNo: entry.serialNo,
    chainId: entry.chainId,
    deviceType: entry.deviceType,
    clientIdOfDrying: entry.clientId,
    modelVersion: entry.modelVersion,

    // Context, so the score survives a recompute of the prediction it judged.
    ratedTrafficLight: entry.trafficLight,
    ratedQualityScore: entry.qualityScore,
    modelSet: entry.modelSet,

    rating,
    ratedAt: now,
    updatedAt: now,
  };

  await reviewsCollection(userKey)
    .doc(renoReviewDocId(entry.serialNo, entry.chainId, entry.modelVersion))
    .set(payload);

  return payload;
}

/**
 * Loads this reviewer's verdict on one drying for one model version, if any.
 *
 * A miss is a normal outcome (most dryings are unrated), so it returns null rather than throwing.
 *
 * @returns The stored review, or null when unrated or stored with a rating this build cannot render.
 */
export async function loadReview(
  userKey: string,
  serialNo: string,
  chainId: string,
  modelVersion: string
): Promise<RenoReview | null> {
  const snapshot = await reviewsCollection(userKey)
    .doc(renoReviewDocId(serialNo, chainId, modelVersion))
    .get();

  if (!snapshot.exists) {
    return null;
  }

  const review = snapshot.data() as RenoReview;

  // A value outside the scale means the document was written by a newer build; showing it as a
  // selected step would be a lie, so it reads as unrated.
  return isValidRenoRating(review.rating) ? review : null;
}

/**
 * Hides a drying from this reviewer's queue, permanently and across every future RENO release.
 *
 * Per reviewer by design (D9): one person hiding a drying has no effect on anyone else's queue.
 */
export async function saveSuppression(
  userKey: string,
  entry: RenoReviewQueueEntry
): Promise<void> {
  const payload: RenoReviewSuppression = {
    userKey,
    serialNo: entry.serialNo,
    chainId: entry.chainId,
    suppressedAt: firestore.Timestamp.now(),
    suppressedUnderModelVersion: entry.modelVersion,
    // Denormalised so the un-hide screen can name the drying without a read per row.
    chainNumber: entry.chainNumber,
    chainStart: entry.chainStart,
  };

  await suppressionsCollection(userKey)
    .doc(renoSuppressionDocId(entry.serialNo, entry.chainId))
    .set(payload);
}

/** Whether this reviewer has hidden the given drying. */
export async function loadSuppression(
  userKey: string,
  serialNo: string,
  chainId: string
): Promise<RenoReviewSuppression | null> {
  const snapshot = await suppressionsCollection(userKey)
    .doc(renoSuppressionDocId(serialNo, chainId))
    .get();

  return snapshot.exists ? (snapshot.data() as RenoReviewSuppression) : null;
}

/** A reviewer's record of everything stepped past. */
function seenCollection(userKey: string) {
  return db
    .collection(RENO_REVIEWERS_COLLECTION)
    .doc(userKey)
    .collection(RENO_SEEN_SUBCOLLECTION);
}

/** A reviewer's free-text remarks and chain reports, one document per drying. */
function notesCollection(userKey: string) {
  return db
    .collection(RENO_REVIEWERS_COLLECTION)
    .doc(userKey)
    .collection(RENO_NOTES_SUBCOLLECTION);
}

/**
 * Saves this reviewer's comment and chain report for one drying.
 *
 * A **separate store from the review** because the rules require a review to carry a rating of 1-4:
 * a reviewer who cannot judge a drying, and says so in a comment, would otherwise have their words
 * refused by the backend. Nothing here is a verdict, so nothing here is validated as one.
 *
 * The comment is trimmed and truncated rather than rejected. It reaches the rules with a length limit
 * they also enforce, and a reviewer who pastes a wall of text should not discover that on the step
 * after the one where they typed it.
 *
 * @param userKey From {@link renoReviewerKey}. Must identify the signed-in user; the rules enforce it.
 * @param entry The drying the note belongs to.
 * @param note What the reviewer wrote and whether they reported the chain.
 * @returns The document that was written. The caller needs it because the reviewer stays on the drying
 *   after toggling `chainSuspect`: without knowing what actually landed, "is there anything left to
 *   save" would have to re-derive the trim/null/truncate rules applied here, and the next step would
 *   write the same note again.
 */
export async function saveNote(
  userKey: string,
  entry: RenoReviewQueueEntry,
  note: { comment: string | null; chainSuspect: boolean }
): Promise<RenoReviewNote> {
  const { clientId, userId } = splitReviewerKey(userKey);
  const trimmed = (note.comment ?? "").trim();

  const payload: RenoReviewNote = {
    userKey,
    clientId,
    userId,

    serialNo: entry.serialNo,
    chainId: entry.chainId,
    modelVersion: entry.modelVersion,

    // Null rather than "" so a cleared comment reads as absent everywhere, including in exports.
    comment: trimmed === "" ? null : trimmed.slice(0, RENO_COMMENT_MAX_LENGTH),
    chainSuspect: note.chainSuspect,
    updatedAt: firestore.Timestamp.now(),
  };

  await notesCollection(userKey)
    .doc(renoNoteDocId(entry.serialNo, entry.chainId))
    .set(payload);

  return payload;
}

/**
 * Loads this reviewer's note on one drying, if any.
 *
 * No model version, because the note is not scoped to one: a comment written under R2.0 is still what
 * this reviewer thinks of the drying under R3.0.
 *
 * @returns The stored note, or null when the reviewer has never written one.
 */
export async function loadNote(
  userKey: string,
  serialNo: string,
  chainId: string
): Promise<RenoReviewNote | null> {
  const snapshot = await notesCollection(userKey)
    .doc(renoNoteDocId(serialNo, chainId))
    .get();

  return snapshot.exists ? (snapshot.data() as RenoReviewNote) : null;
}

/**
 * Records that the reviewer stepped past a drying, rated or not.
 *
 * Idempotent: the deterministic id means re-visiting a drying updates the timestamp rather than
 * accumulating rows.
 */
export async function saveSeen(
  userKey: string,
  entry: RenoReviewQueueEntry
): Promise<void> {
  const payload: RenoReviewSeen = {
    userKey,
    serialNo: entry.serialNo,
    chainId: entry.chainId,
    modelVersion: entry.modelVersion,
    seenAt: firestore.Timestamp.now(),
  };

  await seenCollection(userKey)
    .doc(renoSeenDocId(entry.serialNo, entry.chainId, entry.modelVersion))
    .set(payload);
}

/**
 * What this reviewer has already done with one device's dryings.
 *
 * `excluded` is the **anti-join Firestore cannot do**: the queue index knows nothing about who has
 * rated what, so the exclusion happens client-side, and the only question is how much gets read.
 * Scoping to a single device keeps that bounded no matter how large the reviewer's history grows —
 * loading the whole history when the tool opens is what stops scaling once someone has worked through
 * a few thousand dryings.
 *
 * `ratingByChainId` backs the opposite request: "show me only what I rated 1-2". A plain object
 * rather than a Map, matching the repo convention — Vue 2 cannot track Map mutations, so Maps are
 * avoided even where the value is transient.
 */
export interface RenoReviewDeviceHistory {
  /** Chain ids to drop from this device's queue outright. */
  excluded: Set<string>;
  /** This reviewer's rating per chain id, for the current model version only. */
  ratingByChainId: Record<string, number>;
  /** Chain ids this reviewer reported as having a broken chain. Read only when a filter needs it. */
  chainSuspectChainIds: Set<string>;
  /** Chain ids this reviewer wrote a comment on. Read only when a filter needs it. */
  commentedChainIds: Set<string>;
  /**
   * Chain ids this reviewer has hidden.
   *
   * Normally these are in {@link excluded} instead and this set is redundant; it is what
   * `onlySuppressed` selects **on**, which is the one pass where a hidden drying is the wanted result
   * rather than the thing being dropped.
   */
  suppressedChainIds: Set<string>;
}

/**
 * Loads this reviewer's history for one device.
 *
 * Suppressions are read **always**: "do not show anymore" is not conditional on a filter being
 * switched on, and the one filter that mentions them (`onlySuppressed`) needs the same documents to
 * select on. Reviews are read when either the hide-rated filter or the rating filter needs them, and
 * only once even if both do. Seen records are read only for the hide-seen filter.
 *
 * Each store is queried on `serialNo` equality alone, with `modelVersion` compared in memory. An
 * equality pair would very likely work off the automatic single-field indexes too, but a wrong guess
 * there is a runtime `FAILED_PRECONDITION`, and one device's rows are few enough that filtering them
 * here costs nothing.
 */
export async function loadDeviceHistory(
  userKey: string,
  serialNo: string,
  filters: RenoReviewFilters
): Promise<RenoReviewDeviceHistory> {
  const needsReviews = filters.hideRated || filters.onlyRatings.length > 0;
  const needsNotes = filters.onlyChainSuspect || filters.onlyWithComment;

  const [suppressions, reviews, seen, notes] = await Promise.all([
    suppressionsCollection(userKey).where("serialNo", "==", serialNo).get(),
    needsReviews
      ? reviewsCollection(userKey).where("serialNo", "==", serialNo).get()
      : null,
    filters.hideSeen
      ? seenCollection(userKey).where("serialNo", "==", serialNo).get()
      : null,
    needsNotes
      ? notesCollection(userKey).where("serialNo", "==", serialNo).get()
      : null,
  ]);

  const excluded = new Set<string>();
  const ratingByChainId: Record<string, number> = {};
  const chainSuspectChainIds = new Set<string>();
  const commentedChainIds = new Set<string>();
  const suppressedChainIds = new Set<string>();

  /** Suppressions carry no version and apply to every release; the other two are per version. */
  const appliesNow = (modelVersion: string | undefined) =>
    modelVersion === undefined || modelVersion === filters.modelVersion;

  suppressions.docs.forEach((doc) => {
    const data = doc.data() as { chainId?: string };

    if (!data.chainId) {
      return;
    }

    suppressedChainIds.add(data.chainId);

    // Dropped unless the pass is specifically looking for hidden dryings, in which case excluding
    // them would empty it: the filter selects on exactly the set that normally subtracts.
    if (!filters.onlySuppressed) {
      excluded.add(data.chainId);
    }
  });

  reviews?.docs.forEach((doc) => {
    const data = doc.data() as {
      chainId?: string;
      modelVersion?: string;
      rating?: number;
    };

    if (!data.chainId || !appliesNow(data.modelVersion)) {
      return;
    }

    if (typeof data.rating === "number") {
      ratingByChainId[data.chainId] = data.rating;
    }

    if (filters.hideRated) {
      excluded.add(data.chainId);
    }
  });

  seen?.docs.forEach((doc) => {
    const data = doc.data() as { chainId?: string; modelVersion?: string };

    if (data.chainId && appliesNow(data.modelVersion)) {
      excluded.add(data.chainId);
    }
  });

  // Notes carry no version in their key, so unlike a review or a seen record they apply as written.
  notes?.docs.forEach((doc) => {
    const data = doc.data() as {
      chainId?: string;
      comment?: string | null;
      chainSuspect?: boolean;
    };

    if (!data.chainId) {
      return;
    }

    if (data.chainSuspect === true) {
      chainSuspectChainIds.add(data.chainId);
    }

    if (typeof data.comment === "string" && data.comment.trim() !== "") {
      commentedChainIds.add(data.chainId);
    }
  });

  return {
    excluded,
    ratingByChainId,
    chainSuspectChainIds,
    commentedChainIds,
    suppressedChainIds,
  };
}

/**
 * The whole of one reviewer's history, keyed for the count.
 *
 * Read whole rather than per device as {@link loadDeviceHistory} does, because the count spans every
 * device in the pass: per-device reads would be one query per device, thousands of them. Each store is
 * read with no `where` clause at all — a reviewer's own subcollection is already exactly the scope
 * wanted, so there is no index question and filtering is free in memory.
 *
 * The two truncation flags are separate because the **same** truncated store means opposite things to
 * the two counting strategies: reviews are the candidate set for a revisit pass (truncating them
 * understates) and a subtrahend everywhere else (truncating them overstates).
 */
interface CountHistory {
  /** Every drying this reviewer hid. Version-independent, so it always applies. */
  suppressed: Set<string>;
  /** Rating per queue document id, current model version only. */
  ratingByKey: Record<string, number>;
  /** The same reviews as a key set, for the candidate strategy. */
  rated: Set<string>;
  /** Every drying stepped past, current model version only. */
  seen: Set<string>;
  /** Every drying this reviewer reported as broken. Read only when a filter needs it. */
  chainSuspect: Set<string>;
  /** Every drying this reviewer commented on. Read only when a filter needs it. */
  commented: Set<string>;
  /** The reviews store hit {@link REVIEW_HISTORY_CAP}. */
  reviewsTruncated: boolean;
  /** The notes store hit {@link REVIEW_HISTORY_CAP}. */
  notesTruncated: boolean;
  /**
   * The suppressions store hit {@link REVIEW_HISTORY_CAP}.
   *
   * Tracked separately from {@link exclusionsTruncated} for the same reason the reviews flag is: this
   * one store is a subtrahend in an ordinary pass (truncating it **overstates**) and the candidate set
   * of an `onlySuppressed` pass (truncating it **understates**), so one flag cannot say which way the
   * figure errs.
   */
  suppressionsTruncated: boolean;
  /** The suppressions or seen store hit {@link REVIEW_HISTORY_CAP}. */
  exclusionsTruncated: boolean;
}

/**
 * Loads the reviewer's stores needed to subtract what a pass will not show.
 *
 * Suppressions are read **always** — a hide is not conditional on a filter — while reviews and seen
 * are read only when a filter needs them, exactly as {@link loadDeviceHistory} does.
 */
async function loadCountHistory(
  userKey: string,
  filters: RenoReviewFilters
): Promise<CountHistory> {
  const needsReviews = filters.hideRated || filters.onlyRatings.length > 0;
  const needsNotes = filters.onlyChainSuspect || filters.onlyWithComment;

  const [suppressions, reviews, seen, notes] = await Promise.all([
    suppressionsCollection(userKey).limit(REVIEW_HISTORY_CAP).get(),
    needsReviews ? reviewsCollection(userKey).limit(REVIEW_HISTORY_CAP).get() : null,
    filters.hideSeen ? seenCollection(userKey).limit(REVIEW_HISTORY_CAP).get() : null,
    needsNotes ? notesCollection(userKey).limit(REVIEW_HISTORY_CAP).get() : null,
  ]);

  const suppressed = new Set<string>();
  const ratingByKey: Record<string, number> = {};
  const rated = new Set<string>();
  const seenKeys = new Set<string>();
  const chainSuspect = new Set<string>();
  const commented = new Set<string>();

  /** Suppressions carry no version and apply to every release; the other two are per version. */
  const appliesNow = (modelVersion: string | undefined) =>
    modelVersion === undefined || modelVersion === filters.modelVersion;

  suppressions.docs.forEach((doc) => {
    const data = doc.data() as { serialNo?: string; chainId?: string };

    if (data.serialNo && data.chainId) {
      suppressed.add(renoReviewQueueDocId(data.serialNo, data.chainId));
    }
  });

  reviews?.docs.forEach((doc) => {
    const data = doc.data() as {
      serialNo?: string;
      chainId?: string;
      modelVersion?: string;
      rating?: number;
    };

    if (
      !data.serialNo ||
      !data.chainId ||
      !appliesNow(data.modelVersion) ||
      typeof data.rating !== "number"
    ) {
      return;
    }

    const key = renoReviewQueueDocId(data.serialNo, data.chainId);

    ratingByKey[key] = data.rating;
    rated.add(key);
  });

  seen?.docs.forEach((doc) => {
    const data = doc.data() as {
      serialNo?: string;
      chainId?: string;
      modelVersion?: string;
    };

    if (data.serialNo && data.chainId && appliesNow(data.modelVersion)) {
      seenKeys.add(renoReviewQueueDocId(data.serialNo, data.chainId));
    }
  });

  notes?.docs.forEach((doc) => {
    const data = doc.data() as {
      serialNo?: string;
      chainId?: string;
      comment?: string | null;
      chainSuspect?: boolean;
    };

    if (!data.serialNo || !data.chainId) {
      return;
    }

    const key = renoReviewQueueDocId(data.serialNo, data.chainId);

    if (data.chainSuspect === true) {
      chainSuspect.add(key);
    }

    if (typeof data.comment === "string" && data.comment.trim() !== "") {
      commented.add(key);
    }
  });

  return {
    suppressed,
    ratingByKey,
    rated,
    seen: seenKeys,
    chainSuspect,
    commented,
    reviewsTruncated: reviews != null && reviews.size >= REVIEW_HISTORY_CAP,
    notesTruncated: notes != null && notes.size >= REVIEW_HISTORY_CAP,
    suppressionsTruncated: suppressions.size >= REVIEW_HISTORY_CAP,
    exclusionsTruncated:
      suppressions.size >= REVIEW_HISTORY_CAP ||
      (seen != null && seen.size >= REVIEW_HISTORY_CAP),
  };
}

/**
 * The filters Firestore cannot express, applied to one drying.
 *
 * This is the anti-join, and it is the whole reason the count reads the reviewer's stores at all: a
 * pass hides suppressed dryings unless it was asked for exactly those, and hides or keeps rated,
 * seen, reported and commented ones depending on the filter. A count that skipped this reported work
 * the stepper would never show.
 */
function isHiddenFromPass(
  key: string,
  filters: RenoReviewFilters,
  history: CountHistory
): boolean {
  // The one filter that inverts a subtraction rather than adding one: a hidden-only pass keeps exactly
  // what every other pass drops.
  if (filters.onlySuppressed) {
    if (!history.suppressed.has(key)) {
      return true;
    }
  } else if (history.suppressed.has(key)) {
    return true;
  }

  const rating = history.ratingByKey[key];

  if (filters.hideRated && rating !== undefined) {
    return true;
  }

  if (
    filters.onlyRatings.length > 0 &&
    (rating === undefined || !filters.onlyRatings.includes(rating))
  ) {
    return true;
  }

  if (filters.onlyChainSuspect && !history.chainSuspect.has(key)) {
    return true;
  }

  if (filters.onlyWithComment && !history.commented.has(key)) {
    return true;
  }

  return filters.hideSeen && history.seen.has(key);
}

/**
 * Whether one queue entry passes **every** filter that lives on the entry itself.
 *
 * Checked in memory even for the filter that was pushed to the server. Deliberately: which filter goes
 * to the server is chosen for selectivity and changes with the combination, so making correctness
 * depend on that choice is how a filter silently stops applying. Here it only decides how much the cap
 * has to cover.
 */
function matchesEntryFilters(
  entry: RenoReviewQueueEntry,
  filters: RenoReviewFilters,
  startAtSerial?: string | null
): boolean {
  if (entry.modelVersion !== filters.modelVersion) {
    return false;
  }

  if (
    filters.deviceTypes.length > 0 &&
    !filters.deviceTypes.includes(entry.deviceType)
  ) {
    return false;
  }

  if (filters.clientId && entry.clientId !== filters.clientId) {
    return false;
  }

  const cutoff = chainStartCutoff(filters);

  if (cutoff) {
    // Age wins over active-only, as in `loadChainsForDevice`: they share one query slot.
    if (entry.chainStart.toDate() < cutoff) {
      return false;
    }
  } else if (filters.activeOnly && !entry.isActive) {
    return false;
  }

  // The cursor is inclusive and lexicographic, matching `startAt` on the device track.
  return !startAtSerial || entry.serialNo >= startAtSerial;
}

/**
 * Fetches queue entries by document id, ten at a time.
 *
 * `documentId() in [...]` is served by the built-in name index, so this needs no deployed index. Ten
 * is Firestore's `in` limit, and the chunks run concurrently, so the cost is one round trip per ten
 * dryings rather than per drying.
 */
async function loadQueueEntriesByKey(
  keys: string[]
): Promise<RenoReviewQueueEntry[]> {
  const chunks: string[][] = [];

  for (let index = 0; index < keys.length; index += MAX_IN_VALUES) {
    chunks.push(keys.slice(index, index + MAX_IN_VALUES));
  }

  const snapshots = await Promise.all(
    chunks.map((chunk) =>
      queueCollection()
        .where(firestore.FieldPath.documentId(), "in", chunk)
        .get()
    )
  );

  const entries: RenoReviewQueueEntry[] = [];

  snapshots.forEach((snapshot) => {
    snapshot.docs.forEach((doc) => {
      entries.push(doc.data() as RenoReviewQueueEntry);
    });
  });

  return entries;
}

/**
 * Counts a pass restricted to the reviewer's own marks ("only the ones I rated 1-2", "only the ones I
 * hid") from the store that holds those marks.
 *
 * **Scanning the queue cannot answer this.** A reviewer's own documents are scattered across the whole
 * fleet, so any capped window over a 34k-document queue would contain almost none of them and report
 * a handful instead of the real total. Starting from the marks inverts that: the candidate set is
 * one document per mark, which is small by construction.
 *
 * Every candidate's queue entry is then fetched, which is what makes the age, active and device-type
 * filters exact here, and also drops ratings whose drying Assist has since removed from the index —
 * dryings the stepper would not show either.
 */
async function countCandidatePass(
  filters: RenoReviewFilters,
  startAtSerial: string | null | undefined,
  history: CountHistory
): Promise<RenoReviewQueueCount> {
  // The candidate set is the union of whichever of the reviewer's own stores the filters name, so a
  // pass restricted by rating AND by a note starts from both and `isHiddenFromPass` intersects them.
  const keys = new Set<string>();

  if (filters.onlyRatings.length > 0) {
    history.rated.forEach((key) => keys.add(key));
  }

  if (filters.onlyChainSuspect) {
    history.chainSuspect.forEach((key) => keys.add(key));
  }

  if (filters.onlyWithComment) {
    history.commented.forEach((key) => keys.add(key));
  }

  if (filters.onlySuppressed) {
    history.suppressed.forEach((key) => keys.add(key));
  }

  const candidates = Array.from(keys)
    .filter((key) => !isHiddenFromPass(key, filters, history))
    // Applied before the fetch, so devices before the cursor cost nothing. The serial is the part of
    // the key before the separator, which is how Assist builds it.
    .filter(
      (key) => !startAtSerial || key.slice(0, key.indexOf("__")) >= startAtSerial
    );

  // Only a store this pass draws its candidates from can understate the figure. `suppressionsTruncated`
  // counts here for that reason and nowhere else: everywhere else that same store is a subtrahend, and
  // truncating it makes the figure high rather than low.
  const capped =
    history.reviewsTruncated ||
    history.notesTruncated ||
    (filters.onlySuppressed && history.suppressionsTruncated) ||
    candidates.length > REVIEW_COUNT_CAP;

  const entries = await loadQueueEntriesByKey(
    candidates.slice(0, REVIEW_COUNT_CAP)
  );

  const count = entries.filter((entry) =>
    matchesEntryFilters(entry, filters, startAtSerial)
  ).length;

  return { count, capped, approximate: history.exclusionsTruncated };
}

/**
 * Counts an ordinary pass by scanning the queue, capped.
 *
 * One filter goes to the server and the rest are applied in memory. Which one goes depends on what
 * narrows most, because only the server-side filter escapes the cap: `isActive` covers ~1.9% of the
 * queue and an age window a similar slice, so both make the count exact in practice, while a pass
 * with no narrowing filter is the one case that genuinely cannot be counted and reports "über 1000".
 *
 * Each shape is `where` plus `orderBy` on the **same** field, or on `__name__`, which is what the
 * automatic single-field indexes serve — so no combination here needs a deployed index.
 */
async function countScannedPass(
  filters: RenoReviewFilters,
  startAtSerial: string | null | undefined,
  history: CountHistory
): Promise<RenoReviewQueueCount> {
  let query: firestore.Query = queueCollection();
  /** Whether the cursor could be pushed to the server, or has to be re-checked in memory. */
  let cursorOnServer = false;
  const cutoff = chainStartCutoff(filters);

  if (filters.clientId) {
    // The most selective filter available whenever it is set: one client holds a small slice of a
    // fleet-wide queue, so this is usually what makes the figure exact rather than capped.
    query = query
      .where("clientId", "==", filters.clientId)
      .orderBy(firestore.FieldPath.documentId());
    cursorOnServer = true;
  } else if (cutoff) {
    // A range filter must be the first `orderBy`, which is why the cursor stays in memory here.
    query = query.where("chainStart", ">=", cutoff).orderBy("chainStart", "desc");
  } else if (filters.activeOnly) {
    query = query
      .where("isActive", "==", true)
      .orderBy(firestore.FieldPath.documentId());
    cursorOnServer = true;
  } else {
    query = query
      .where("modelVersion", "==", filters.modelVersion)
      .orderBy(firestore.FieldPath.documentId());
    cursorOnServer = true;
  }

  if (cursorOnServer && startAtSerial) {
    // Queue ids are `{serialNo}__{chainId}`, so a bare serial sorts just before that device's first
    // drying: the same inclusive cursor the device track uses.
    query = query.startAt(startAtSerial);
  }

  const snapshot = await query.limit(REVIEW_COUNT_CAP).get();

  let count = 0;

  snapshot.docs.forEach((doc) => {
    const entry = doc.data() as RenoReviewQueueEntry;
    const key = renoReviewQueueDocId(entry.serialNo, entry.chainId);

    if (
      matchesEntryFilters(entry, filters, startAtSerial) &&
      !isHiddenFromPass(key, filters, history)
    ) {
      count += 1;
    }
  });

  return {
    count,
    capped: snapshot.size >= REVIEW_COUNT_CAP,
    approximate: history.reviewsTruncated || history.exclusionsTruncated,
  };
}

/**
 * Counts the dryings a pass with these filters will actually show.
 *
 * **Must be given the same `startAtSerial` as {@link loadReviewDevices}.** Counting without the cursor
 * while the traversal applies it is how the screen ends up promising work the stepper cannot find: a
 * serial sorting past the last device yields a confident count and an empty screen. The count answers
 * "how much work does *this* pass hold", not "how much exists".
 *
 * Two strategies, picked by whether the pass is restricted to the reviewer's own marks — see
 * {@link countCandidatePass} for why a queue scan cannot answer that one. Both apply the reviewer's
 * suppressions, ratings and seen records, which is the anti-join Firestore has no operator for.
 *
 * @param userKey From `renoReviewerKey`. The stores read are this reviewer's own.
 * @param filters Reviewer's filter selection.
 * @param startAtSerial Serial to resume from, inclusive.
 * @returns The count, and whether it is a lower bound or slightly high.
 */
export async function countReviewQueue(
  userKey: string,
  filters: RenoReviewFilters,
  startAtSerial?: string | null
): Promise<RenoReviewQueueCount> {
  const history = await loadCountHistory(userKey, filters);

  // A filter that names the reviewer's own documents makes those documents the candidate set. Scanning
  // the queue for them cannot work: they are scattered across the whole fleet, so a capped window
  // holds almost none of them.
  const startsFromOwnStores =
    filters.onlyRatings.length > 0 ||
    filters.onlyChainSuspect ||
    filters.onlyWithComment ||
    filters.onlySuppressed;

  return startsFromOwnStores
    ? countCandidatePass(filters, startAtSerial, history)
    : countScannedPass(filters, startAtSerial, history);
}

/** Page size for the un-hide screen. Suppressions are rare, so one page is usually all of them. */
export const SUPPRESSION_PAGE_SIZE = 50;

/**
 * Lists this reviewer's hidden dryings, most recently hidden first.
 *
 * `orderBy` on a single field needs no composite index, so this stays deploy-free. Paged rather than
 * unbounded because nothing stops a reviewer hiding thousands over time, even though in practice
 * suppression should be rare.
 *
 * @param userKey From {@link renoReviewerKey}.
 * @param startAfter The last `suppressedAt` of the previous page, for "load more".
 */
export async function loadSuppressions(
  userKey: string,
  startAfter?: firestore.Timestamp | null
): Promise<RenoReviewSuppression[]> {
  let query: firestore.Query = suppressionsCollection(userKey).orderBy(
    "suppressedAt",
    "desc"
  );

  if (startAfter) {
    query = query.startAfter(startAfter);
  }

  const snapshot = await query.limit(SUPPRESSION_PAGE_SIZE).get();

  return snapshot.docs.map((doc) => doc.data() as RenoReviewSuppression);
}

/**
 * Puts a hidden drying back into this reviewer's queue.
 *
 * Deleting the suppression is the whole mechanism: the queue subtracts whatever is in that store, so
 * removing the row is what makes the drying visible again. Reviews are deliberately NOT deletable —
 * a verdict is a record — but a hide is a preference, and a permanent one with no way back is a
 * support request waiting to happen.
 */
export async function removeSuppression(
  userKey: string,
  serialNo: string,
  chainId: string
): Promise<void> {
  await suppressionsCollection(userKey)
    .doc(renoSuppressionDocId(serialNo, chainId))
    .delete();
}
